import React, { useEffect, useState } from "react";
import { api, downloadAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function BalancePage() {
  const today = new Date();
  const firstOfYear = `${today.getFullYear()}-01-01`;
  const todayStr = today.toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstOfYear);
  const [to, setTo] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/balance", { params: { date_from: from, date_to: to } });
      setData(data);
    } catch (e) {
      toast.error("Erreur de chargement de la balance");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    downloadAuthedFile(`/accounting/export/balance.csv?date_from=${from}&date_to=${to}`, `balance-${from}-${to}.csv`)
      .catch(() => toast.error("Export impossible"));
  };

  return (
    <div className="space-y-6" data-testid="balance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Scale className="h-3 w-3" /><span>BALANCE GÉNÉRALE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Balance générale</h1>
          <p className="text-slate-500 mt-1">Soldes et mouvements de tous les comptes mouvementés sur la période.</p>
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="export-balance"><Download className="h-4 w-4 mr-2" />Export Excel</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Du</Label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="balance-from" />
          </div>
          <div>
            <Label>Au</Label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="balance-to" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="balance-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />Recalculer
          </Button>
        </div>
      </div>

      {data && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className={`px-5 py-3 ${data.balanced ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"} font-semibold text-sm`}>
            {data.balanced ? `✓ Balance équilibrée — Total Débit = Total Crédit = ${fmt(data.total_debit)} GNF` : `✗ Balance déséquilibrée ! Débit ${fmt(data.total_debit)} ≠ Crédit ${fmt(data.total_credit)}`}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Libellé du compte</th>
                  <th className="px-4 py-3">Cl.</th>
                  <th className="px-4 py-3 text-right">Mouvement Débit</th>
                  <th className="px-4 py-3 text-right">Mouvement Crédit</th>
                  <th className="px-4 py-3 text-right">Solde Débit</th>
                  <th className="px-4 py-3 text-right">Solde Crédit</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`balance-row-${r.code}`}>
                    <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-2">{r.name}</td>
                    <td className="px-4 py-2"><span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-xs font-bold">{r.class_num}</span></td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(r.debit)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(r.credit)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#1E3A8A] font-semibold">{r.balance_debit > 0 ? fmt(r.balance_debit) : "-"}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#EA580C] font-semibold">{r.balance_credit > 0 ? fmt(r.balance_credit) : "-"}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucun mouvement sur la période.</td></tr>
                )}
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                  <td colSpan={3} className="px-4 py-3 text-right">TOTAUX</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt(data.total_debit)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt(data.total_credit)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
